import type { SupportedLocale } from "@lib/util/seo"
import { DEFAULT_LOCALE, isSupportedLocale } from "@lib/util/seo"

/**
 * UI string dictionary for static, repeating storefront chrome.
 *
 * Use `t(locale, key)` from server components (read locale from
 * `headers().get("x-locale")`) and the `useT()` hook from client
 * components — see `./use-t.ts`.
 *
 * Add new keys here with translations in all three locales (UA/RU/EN).
 * If a translation is uncertain, leave the original UA + a TODO comment
 * rather than shipping an empty string.
 */
export const DICTIONARY = {
  ua: {
    // navigation
    catalog: "Каталог",
    help: "Допомога",
    delivery: "Доставка і оплата",
    cart: "Кошик",
    cabinet: "Кабінет",
    home: "Головна",
    menu: "Меню",
    closeMenu: "Закрити меню",
    allCategories: "Всі категорії",
    catalogSoon: "Каталог незабаром поповнюватиметься",
    // sidebar
    categories: "Категорії",
    allProducts: "Усі товари",
    // filter chips
    inStock: "В наявності",
    promo: "Акції",
    new: "Новинки",
    // filter labels
    package: "Корпус",
    voltage: "Напруга",
    manufacturer: "Виробник",
    power: "Потужність",
    moreFilters: "+ Фільтри",
    lessFilters: "Менше",
    clearFilters: "Очистити",
    filters: "Фільтри",
    close: "Закрити",
    // sort
    sortBy: "Сортування",
    sortNew: "Новинки",
    sortPriceAsc: "Дешевші спочатку",
    sortPriceDesc: "Дорожчі спочатку",
    sortByName: "За назвою",
    sortPriceUp: "Ціна ↑",
    sortPriceDown: "Ціна ↓",
    // footer headers
    footerCatalog: "Каталог",
    footerCustomers: "Покупцям",
    footerBusiness: "Для бізнесу",
    footerTagline:
      "AI-powered маркетплейс електронних компонентів. 16 000+ товарів, миттєвий пошук, доставка по всій Україні.",
    // footer links — узгоджено зі структурою існуючого footer.tsx
    diodes: "Діоди",
    transistors: "Транзистори",
    ics: "Мікросхеми",
    resistors: "Резистори",
    capacitors: "Конденсатори",
    leds: "Світлодіоди",
    deliveryPay: "Доставка і оплата",
    returns: "Повернення",
    warranty: "Гарантія",
    contacts: "Контакти",
    aboutUs: "Про нас",
    b2bSales: "B2B-продажі",
    becomeSeller: "Стати продавцем",
    apiIntegration: "API-інтеграція",
    partnership: "Партнерство",
    // top bar / generic
    contactPhonePlaceholder: "+380 (XX) XXX-XX-XX",
    // search
    searchPlaceholder:
      "Пошук: транзистор, резистор 10k, блок живлення 12V…",
    // status / misc
    selectCountry: "Оберіть країну",
    rightsReserved: "Всі права захищені.",
    // store / catalog headings
    radioComponents: "Радіоелектронні компоненти",
    productsRangeOf: "з",
    productsWord: "товарів",
    productsNotFound: "Товарів не знайдено",
    // cart-dropdown
    cartEmpty: "Кошик порожній.",
    goToCatalog: "Перейти до каталогу",
    goToCart: "Перейти до кошика",
    quantity: "Кількість",
    remove: "Видалити",
    subtotal: "Сума",
    excludingTaxes: "(без податків)",
    // side-menu
    sideMenuHeading: "Електроніка та комплектуючі",
    sideMenuSub: "Радіодеталі, LED, інструменти та B2B-постачання.",
    // cart-mismatch
    cartTransferError: "Сталася помилка під час перенесення кошика",
    cartTransferRetry: "Спробувати ще раз",
    cartTransferring: "Переносимо…",
    // cart page
    cartTitle: "Кошик",
    cartEmptyTitle: "Кошик порожній",
    cartEmptyBody:
      "У вашому кошику ще нічого немає. Перейдіть до каталогу — підберемо потрібні компоненти.",
    exploreProducts: "До каталогу",
    cartItemColumn: "Товар",
    cartPriceColumn: "Ціна",
    cartTotalColumn: "Сума",
    cartSummaryTitle: "Підсумок замовлення",
    cartCheckout: "Оформити замовлення",
    cartHaveAccount: "Вже маєте акаунт?",
    cartSignInPrompt: "Увійдіть для зручнішого замовлення.",
    cartSignIn: "Увійти",
    // home — hero
    heroBadge: "AI-powered маркетплейс радіоелементів",
    heroTitle1: "Електронні",
    heroTitleAccent: "компоненти",
    heroTitle2: "для ваших проєктів",
    heroSubtext:
      "16 000+ позицій з реальними залишками: транзистори, мікросхеми, резистори, конденсатори. Параметричний пошук, AI-підбір аналогів, відправка Новою Поштою у день оплати.",
    heroCtaCatalog: "Перейти до каталогу →",
    heroCtaAi: "AI-Консультант",
    heroStatProducts: "товарів у каталозі",
    heroStatSpeed: "швидкість пошуку",
    heroStatCategories: "категорій",
    heroStatSupport: "AI-підтримка",
    // home — category grid
    catalogTitle: "Каталог компонентів",
    catalogSubtitle: "Активні і пасивні компоненти з фільтрами за параметрами",
    allCategoriesArrow: "Всі категорії →",
    catalogEmpty: "Категорії наповнюємо — товари вже доступні у каталозі",
    goToProductsArrow: "Перейти до товарів →",
    // home — featured products
    newArrivals: "Нові надходження",
    allProductsArrow: "Усі товари →",
    loadingNew: "Завантажуємо нові позиції — зайдіть у повний каталог",
    allProductsCatalogArrow: "Усі товари в каталозі →",
    sale: "АКЦІЯ",
    pcs: "шт",
    outOfStock: "Немає",
    // seller / marketplace
    sellers: "Продавці",
    seller: "Продавець",
    sellerSince: "з",
    sellerCity: "Місто",
    sellerProducts: "товарів",
    sellerViewAll: "Переглянути всі товари",
    sellerAllProducts: "Усі товари продавця",
    sellerCategories: "Категорії продавця",
    sellerCatalog: "Каталог продавця",
    sellerContacts: "Контакти",
    sellerEmail: "Email",
    sellerPhone: "Телефон",
    sellerLegal: "Юридична інформація",
    sellerNotFound: "Продавця не знайдено",
    sellersIndexTitle: "Продавці маркетплейсу",
    sellersIndexSubtitle: "Магазини, дистриб'ютори та виробники електронних компонентів на платформі Kvatronix",
    sellersEmpty: "Поки що немає активних продавців",
    sellerSearchPlaceholder: "Пошук серед товарів продавця…",
    sellerShowMore: "+ показати ще",
    sellerStockLabel: "В наявності",
    sellerOnOrderLabel: "Під замовлення",
    sellerStockSuffix: "шт",
    // home — ai section
    poweredByAi: "POWERED BY AI",
    aiConsultantTitle: "AI-консультант",
    aiConsultantDesc:
      "Опишіть вашу задачу — AI підбере потрібні компоненти з каталогу. Працює як досвідчений інженер: знає характеристики, аналоги та сумісність деталей.",
    aiFeature1: "💬 «Потрібен конденсатор для фільтра БП 12В, 2А»",
    aiFeature2: "🔍 Підбір аналогів та замін",
    aiFeature3: "📋 Рекомендації з даташитів",
    aiFeature4: "🇺🇦 Відповіді українською",
    aiAssistantName: "AI-помічник Kvatronix",
    online: "Онлайн",
    aiGreeting:
      "Опишіть задачу — підкажу компонент із каталогу, аналог або параметри.",
    aiChip1: "Підбери MOSFET для ШІМ 24В 5А",
    aiChip2: "Аналог BC547",
    aiChip3: "1N4007 в наявності?",
    aiInputPlaceholder: "Опишіть що вам потрібно...",
    aiSendAria: "Надіслати",
    aiRateLimitPrefix: "Перевищено ліміт. Зачекайте",
    aiRateLimitSuffix: "с",
    // home — why kvatronix
    whyTitle: "Чому Kvatronix?",
    whySubtitle: "Сучасна платформа для професіоналів і ентузіастів",
    whyItem1Title: "Пошук за 2ms",
    whyItem1Desc: "MeiliSearch з фасетними фільтрами та пошуком по парт-номеру",
    whyItem2Title: "AI-опис товарів",
    whyItem2Desc:
      "GPT-4o генерує описи, підбирає аналоги та рекомендує сумісні деталі",
    whyItem3Title: "Нова Пошта",
    whyItem3Desc:
      "Автоматичний розрахунок доставки, створення ТТН, трекінг посилок",
    whyItem4Title: "Мультиканальність",
    whyItem4Desc:
      "Продаємо на Rozetka, Prom.ua, Hotline та Google Shopping одночасно",
    // checkout (call-to-order MVP)
    coTitle: "Оформлення замовлення",
    coSubtitle: "Менеджер зателефонує протягом 30 хв — узгодимо доставку та оплату телефоном.",
    coBackToCart: "← До кошика",
    coContactInfo: "Контакти",
    coName: "Імʼя та прізвище",
    coNamePlaceholder: "Іван Петренко",
    coPhone: "Телефон",
    coEmail: "Email (необовʼязково)",
    coEmailHint: "Якщо вкажете — продублюємо деталі замовлення на email.",
    coDelivery: "Доставка",
    coCity: "Місто",
    coCityPlaceholder: "Київ",
    coNotes: "Коментар",
    coNotesPlaceholder: "Відділення Нової Пошти, вимоги до пакування тощо",
    coTerms: "Погоджуюсь з умовами використання та політикою конфіденційності",
    coSubmit: "Оформити замовлення",
    coSubmitting: "Надсилаємо…",
    coSummary: "Ваше замовлення",
    coTotal: "Разом",
    coError: "Не вдалося оформити замовлення. Спробуйте ще раз або зателефонуйте: +380 99 042-21-13",
    coEmptyCart: "Кошик порожній",
    coGoCatalog: "До каталогу",
    coRequiredField: "обовʼязкове поле",
    coInvalidPhone: "Невірний формат телефону",
    // order confirmation
    orderThanks: "Замовлення прийнято!",
    orderCallback: "Менеджер зателефонує протягом 30 хв — узгодимо доставку та оплату.",
    orderNumber: "Номер замовлення",
    orderDate: "Дата",
    orderSummary: "Склад замовлення",
    orderDelivery: "Доставка",
    orderShippingAddress: "Адреса доставки",
    orderContact: "Контакт",
    orderShippingMethod: "Спосіб доставки",
    orderPayment: "Оплата",
    orderPaymentMethod: "Спосіб оплати",
    orderPaymentManual: "Оплата за домовленістю з менеджером",
    orderPaymentDue: "Сума до сплати",
    orderHelpTitle: "Маєте питання?",
    orderHelpCall: "Телефон",
    orderHelpContacts: "Контакти",
    orderHelpReturns: "Повернення та обмін",
    orderBackToCatalog: "До каталогу",
    // cart totals (translated)
    ctSubtotal: "Сума",
    ctShipping: "Доставка",
    ctTaxes: "Податки",
    ctDiscount: "Знижка",
    ctTotal: "Разом",
    // checkout chrome
    chkBackToCart: "← До кошика",
    chkRights: "Усі права захищені.",
    // PDP
    pdpDescEmpty: "Опис від виробника надається на запит — зверніться до менеджера.",
    pdpDatasheet: "Даташит PDF",
    pdpCopySku: "Скопіювати артикул",
    pdpCopied: "Скопійовано",
    pdpDeliveryTitle: "Доставка та умови",
    pdpDeliveryNova: "Новою Поштою у день оплати",
    pdpDeliveryPickup: "Самовивіз з Києва",
    pdpDeliveryCallback: "Менеджер передзвонить для уточнення",
    pdpTrustWarranty: "Гарантія",
    pdpTrustOriginal: "Оригінальні компоненти",
    pdpTrustConsult: "Технічна консультація",
    pdpAddToCart: "Додати до кошика",
    pdpSelectVariant: "Оберіть варіант",
    pdpOutOfStock: "Немає в наявності",
    pdpSelectOptions: "Оберіть опції",
    pdpQty: "Кількість",
  },
  ru: {
    // navigation
    catalog: "Каталог",
    help: "Помощь",
    delivery: "Доставка и оплата",
    cart: "Корзина",
    cabinet: "Кабинет",
    home: "Главная",
    menu: "Меню",
    closeMenu: "Закрыть меню",
    allCategories: "Все категории",
    catalogSoon: "Каталог скоро пополнится",
    // sidebar
    categories: "Категории",
    allProducts: "Все товары",
    // filter chips
    inStock: "В наличии",
    promo: "Акции",
    new: "Новинки",
    // filter labels
    package: "Корпус",
    voltage: "Напряжение",
    manufacturer: "Производитель",
    power: "Мощность",
    moreFilters: "+ Фильтры",
    lessFilters: "Меньше",
    clearFilters: "Очистить",
    filters: "Фильтры",
    close: "Закрыть",
    // sort
    sortBy: "Сортировка",
    sortNew: "Новинки",
    sortPriceAsc: "Сначала дешевле",
    sortPriceDesc: "Сначала дороже",
    sortByName: "По названию",
    sortPriceUp: "Цена ↑",
    sortPriceDown: "Цена ↓",
    // footer headers
    footerCatalog: "Каталог",
    footerCustomers: "Покупателям",
    footerBusiness: "Для бизнеса",
    footerTagline:
      "AI-powered маркетплейс электронных компонентов. 16 000+ товаров, мгновенный поиск, доставка по всей Украине.",
    // footer links
    diodes: "Диоды",
    transistors: "Транзисторы",
    ics: "Микросхемы",
    resistors: "Резисторы",
    capacitors: "Конденсаторы",
    leds: "Светодиоды",
    deliveryPay: "Доставка и оплата",
    returns: "Возврат",
    warranty: "Гарантия",
    contacts: "Контакты",
    aboutUs: "О нас",
    b2bSales: "B2B-продажи",
    becomeSeller: "Стать продавцом",
    apiIntegration: "API-интеграция",
    partnership: "Партнёрство",
    // top bar / generic
    contactPhonePlaceholder: "+380 (XX) XXX-XX-XX",
    // search
    searchPlaceholder:
      "Поиск: транзистор, резистор 10к, блок питания 12В…",
    // status / misc
    selectCountry: "Выберите страну",
    rightsReserved: "Все права защищены.",
    // store / catalog headings
    radioComponents: "Радиоэлектронные компоненты",
    productsRangeOf: "из",
    productsWord: "товаров",
    productsNotFound: "Товары не найдены",
    // cart-dropdown
    cartEmpty: "Корзина пуста.",
    goToCatalog: "Перейти в каталог",
    goToCart: "Перейти в корзину",
    quantity: "Количество",
    remove: "Удалить",
    subtotal: "Сумма",
    excludingTaxes: "(без налогов)",
    // side-menu
    sideMenuHeading: "Электроника и комплектующие",
    sideMenuSub: "Радиодетали, LED, инструменты и B2B-поставки.",
    // cart-mismatch
    cartTransferError: "Произошла ошибка при переносе корзины",
    cartTransferRetry: "Попробовать ещё раз",
    cartTransferring: "Переносим…",
    // cart page
    cartTitle: "Корзина",
    cartEmptyTitle: "Корзина пуста",
    cartEmptyBody:
      "В вашей корзине пока ничего нет. Перейдите в каталог — подберём нужные компоненты.",
    exploreProducts: "В каталог",
    cartItemColumn: "Товар",
    cartPriceColumn: "Цена",
    cartTotalColumn: "Сумма",
    cartSummaryTitle: "Итог заказа",
    cartCheckout: "Оформить заказ",
    cartHaveAccount: "Уже есть аккаунт?",
    cartSignInPrompt: "Войдите для удобного оформления заказа.",
    cartSignIn: "Войти",
    // home — hero
    heroBadge: "AI-powered маркетплейс радиоэлементов",
    heroTitle1: "Электронные",
    heroTitleAccent: "компоненты",
    heroTitle2: "для ваших проектов",
    heroSubtext:
      "16 000+ позиций с реальными остатками: транзисторы, микросхемы, резисторы, конденсаторы. Параметрический поиск, AI-подбор аналогов, отправка Новой Почтой в день оплаты.",
    heroCtaCatalog: "Перейти в каталог →",
    heroCtaAi: "AI-Консультант",
    heroStatProducts: "товаров в каталоге",
    heroStatSpeed: "скорость поиска",
    heroStatCategories: "категорий",
    heroStatSupport: "AI-поддержка",
    // home — category grid
    catalogTitle: "Каталог компонентов",
    catalogSubtitle: "Активные и пассивные компоненты с фильтрами по параметрам",
    allCategoriesArrow: "Все категории →",
    catalogEmpty: "Категории наполняем — товары уже доступны в каталоге",
    goToProductsArrow: "Перейти к товарам →",
    // home — featured products
    newArrivals: "Новые поступления",
    allProductsArrow: "Все товары →",
    loadingNew: "Загружаем новые позиции — зайдите в полный каталог",
    allProductsCatalogArrow: "Все товары в каталоге →",
    sale: "АКЦИЯ",
    pcs: "шт",
    outOfStock: "Нет",
    // seller / marketplace
    sellers: "Продавцы",
    seller: "Продавец",
    sellerSince: "с",
    sellerCity: "Город",
    sellerProducts: "товаров",
    sellerViewAll: "Посмотреть все товары",
    sellerAllProducts: "Все товары продавца",
    sellerCategories: "Категории продавца",
    sellerCatalog: "Каталог продавца",
    sellerContacts: "Контакты",
    sellerEmail: "Email",
    sellerPhone: "Телефон",
    sellerLegal: "Юридическая информация",
    sellerNotFound: "Продавец не найден",
    sellersIndexTitle: "Продавцы маркетплейса",
    sellersIndexSubtitle: "Магазины, дистрибьюторы и производители электронных компонентов на платформе Kvatronix",
    sellersEmpty: "Пока нет активных продавцов",
    sellerSearchPlaceholder: "Поиск среди товаров продавца…",
    sellerShowMore: "+ показать ещё",
    sellerStockLabel: "В наличии",
    sellerOnOrderLabel: "Под заказ",
    sellerStockSuffix: "шт",
    // home — ai section
    poweredByAi: "POWERED BY AI",
    aiConsultantTitle: "AI-консультант",
    aiConsultantDesc:
      "Опишите вашу задачу — AI подберёт нужные компоненты из каталога. Работает как опытный инженер: знает характеристики, аналоги и совместимость деталей.",
    aiFeature1: "💬 «Нужен конденсатор для фильтра БП 12В, 2А»",
    aiFeature2: "🔍 Подбор аналогов и замен",
    aiFeature3: "📋 Рекомендации по даташитам",
    aiFeature4: "🇺🇦 Ответы на украинском",
    aiAssistantName: "AI-помощник Kvatronix",
    online: "Онлайн",
    aiGreeting:
      "Опишите задачу — подскажу компонент из каталога, аналог или параметры.",
    aiChip1: "Подбери MOSFET для ШИМ 24В 5А",
    aiChip2: "Аналог BC547",
    aiChip3: "1N4007 в наличии?",
    aiInputPlaceholder: "Опишите, что вам нужно...",
    aiSendAria: "Отправить",
    aiRateLimitPrefix: "Превышен лимит. Подождите",
    aiRateLimitSuffix: "с",
    // home — why kvatronix
    whyTitle: "Почему Kvatronix?",
    whySubtitle: "Современная платформа для профессионалов и энтузиастов",
    whyItem1Title: "Поиск за 2ms",
    whyItem1Desc: "MeiliSearch с фасетными фильтрами и поиском по парт-номеру",
    whyItem2Title: "AI-описание товаров",
    whyItem2Desc:
      "GPT-4o генерирует описания, подбирает аналоги и рекомендует совместимые детали",
    whyItem3Title: "Новая Почта",
    whyItem3Desc:
      "Автоматический расчёт доставки, создание ТТН, трекинг посылок",
    whyItem4Title: "Мультиканальность",
    whyItem4Desc:
      "Продаём на Rozetka, Prom.ua, Hotline и Google Shopping одновременно",
    // checkout (call-to-order MVP)
    coTitle: "Оформление заказа",
    coSubtitle: "Менеджер позвонит в течение 30 мин — согласуем доставку и оплату по телефону.",
    coBackToCart: "← В корзину",
    coContactInfo: "Контакты",
    coName: "Имя и фамилия",
    coNamePlaceholder: "Иван Петренко",
    coPhone: "Телефон",
    coEmail: "Email (необязательно)",
    coEmailHint: "Если укажете — продублируем детали заказа на email.",
    coDelivery: "Доставка",
    coCity: "Город",
    coCityPlaceholder: "Киев",
    coNotes: "Комментарий",
    coNotesPlaceholder: "Отделение Новой Почты, требования к упаковке и т.д.",
    coTerms: "Согласен с условиями использования и политикой конфиденциальности",
    coSubmit: "Оформить заказ",
    coSubmitting: "Отправляем…",
    coSummary: "Ваш заказ",
    coTotal: "Итого",
    coError: "Не удалось оформить заказ. Попробуйте ещё раз или позвоните: +380 99 042-21-13",
    coEmptyCart: "Корзина пуста",
    coGoCatalog: "В каталог",
    coRequiredField: "обязательное поле",
    coInvalidPhone: "Неверный формат телефона",
    // order confirmation
    orderThanks: "Заказ принят!",
    orderCallback: "Менеджер позвонит в течение 30 мин — согласуем доставку и оплату.",
    orderNumber: "Номер заказа",
    orderDate: "Дата",
    orderSummary: "Состав заказа",
    orderDelivery: "Доставка",
    orderShippingAddress: "Адрес доставки",
    orderContact: "Контакт",
    orderShippingMethod: "Способ доставки",
    orderPayment: "Оплата",
    orderPaymentMethod: "Способ оплаты",
    orderPaymentManual: "Оплата по согласованию с менеджером",
    orderPaymentDue: "Сумма к оплате",
    orderHelpTitle: "Есть вопросы?",
    orderHelpCall: "Телефон",
    orderHelpContacts: "Контакты",
    orderHelpReturns: "Возврат и обмен",
    orderBackToCatalog: "В каталог",
    // cart totals
    ctSubtotal: "Сумма",
    ctShipping: "Доставка",
    ctTaxes: "Налоги",
    ctDiscount: "Скидка",
    ctTotal: "Итого",
    // checkout chrome
    chkBackToCart: "← В корзину",
    chkRights: "Все права защищены.",
    // PDP
    pdpDescEmpty: "Описание от производителя предоставляется по запросу — обратитесь к менеджеру.",
    pdpDatasheet: "Даташит PDF",
    pdpCopySku: "Скопировать артикул",
    pdpCopied: "Скопировано",
    pdpDeliveryTitle: "Доставка и условия",
    pdpDeliveryNova: "Новой Почтой в день оплаты",
    pdpDeliveryPickup: "Самовывоз из Киева",
    pdpDeliveryCallback: "Менеджер перезвонит для уточнения",
    pdpTrustWarranty: "Гарантия",
    pdpTrustOriginal: "Оригинальные компоненты",
    pdpTrustConsult: "Техническая консультация",
    pdpAddToCart: "Добавить в корзину",
    pdpSelectVariant: "Выберите вариант",
    pdpOutOfStock: "Нет в наличии",
    pdpSelectOptions: "Выберите опции",
    pdpQty: "Количество",
  },
  en: {
    // navigation
    catalog: "Catalog",
    help: "Help",
    delivery: "Delivery & Payment",
    cart: "Cart",
    cabinet: "Account",
    home: "Home",
    menu: "Menu",
    closeMenu: "Close menu",
    allCategories: "All categories",
    catalogSoon: "Catalog will be expanded soon",
    // sidebar
    categories: "Categories",
    allProducts: "All products",
    // filter chips
    inStock: "In stock",
    promo: "Sale",
    new: "New",
    // filter labels
    package: "Package",
    voltage: "Voltage",
    manufacturer: "Manufacturer",
    power: "Power",
    moreFilters: "+ Filters",
    lessFilters: "Less",
    clearFilters: "Clear",
    filters: "Filters",
    close: "Close",
    // sort
    sortBy: "Sort by",
    sortNew: "Newest",
    sortPriceAsc: "Price: low to high",
    sortPriceDesc: "Price: high to low",
    sortByName: "By name",
    sortPriceUp: "Price ↑",
    sortPriceDown: "Price ↓",
    // footer headers
    footerCatalog: "Catalog",
    footerCustomers: "For customers",
    footerBusiness: "For business",
    footerTagline:
      "AI-powered marketplace for electronic components. 16,000+ products, instant search, delivery across Ukraine.",
    // footer links
    diodes: "Diodes",
    transistors: "Transistors",
    ics: "ICs",
    resistors: "Resistors",
    capacitors: "Capacitors",
    leds: "LEDs",
    deliveryPay: "Delivery & Payment",
    returns: "Returns",
    warranty: "Warranty",
    contacts: "Contacts",
    aboutUs: "About us",
    b2bSales: "B2B Sales",
    becomeSeller: "Become a seller",
    apiIntegration: "API integration",
    partnership: "Partnership",
    // top bar / generic
    contactPhonePlaceholder: "+380 (XX) XXX-XX-XX",
    // search
    searchPlaceholder:
      "Search: transistor, resistor 10k, power supply 12V…",
    // status / misc
    selectCountry: "Select country",
    rightsReserved: "All rights reserved.",
    // store / catalog headings
    radioComponents: "Electronic components",
    productsRangeOf: "of",
    productsWord: "products",
    productsNotFound: "No products found",
    // cart-dropdown
    cartEmpty: "Your cart is empty.",
    goToCatalog: "Go to catalog",
    goToCart: "Go to cart",
    quantity: "Quantity",
    remove: "Remove",
    subtotal: "Subtotal",
    excludingTaxes: "(excl. taxes)",
    // side-menu
    sideMenuHeading: "Electronics & components",
    sideMenuSub: "Radio parts, LEDs, tools and B2B supply.",
    // cart-mismatch
    cartTransferError: "Something went wrong while transferring your cart",
    cartTransferRetry: "Try again",
    cartTransferring: "Transferring…",
    // cart page
    cartTitle: "Cart",
    cartEmptyTitle: "Your cart is empty",
    cartEmptyBody:
      "You don't have anything in your cart yet. Browse the catalog and we'll help pick the right parts.",
    exploreProducts: "Browse catalog",
    cartItemColumn: "Item",
    cartPriceColumn: "Price",
    cartTotalColumn: "Total",
    cartSummaryTitle: "Order summary",
    cartCheckout: "Place order",
    cartHaveAccount: "Already have an account?",
    cartSignInPrompt: "Sign in for a better experience.",
    cartSignIn: "Sign in",
    // home — hero
    heroBadge: "AI-powered marketplace for radio components",
    heroTitle1: "Electronic",
    heroTitleAccent: "components",
    heroTitle2: "for your projects",
    heroSubtext:
      "16,000+ items with real-time stock: transistors, ICs, resistors, capacitors. Parametric search, AI analog matching, Nova Poshta delivery on the day of payment.",
    heroCtaCatalog: "Browse catalog →",
    heroCtaAi: "AI Consultant",
    heroStatProducts: "products in catalog",
    heroStatSpeed: "search speed",
    heroStatCategories: "categories",
    heroStatSupport: "AI support",
    // home — category grid
    catalogTitle: "Components catalog",
    catalogSubtitle: "Active and passive components with parametric filters",
    allCategoriesArrow: "All categories →",
    catalogEmpty: "Categories are being populated — products are already in the catalog",
    goToProductsArrow: "Go to products →",
    // home — featured products
    newArrivals: "New arrivals",
    allProductsArrow: "All products →",
    loadingNew: "Loading new items — visit the full catalog",
    allProductsCatalogArrow: "All products in catalog →",
    sale: "SALE",
    pcs: "pcs",
    outOfStock: "Out of stock",
    // seller / marketplace
    sellers: "Sellers",
    seller: "Seller",
    sellerSince: "since",
    sellerCity: "City",
    sellerProducts: "products",
    sellerViewAll: "View all products",
    sellerAllProducts: "All seller products",
    sellerCategories: "Seller categories",
    sellerCatalog: "Seller catalog",
    sellerContacts: "Contacts",
    sellerEmail: "Email",
    sellerPhone: "Phone",
    sellerLegal: "Legal information",
    sellerNotFound: "Seller not found",
    sellersIndexTitle: "Marketplace sellers",
    sellersIndexSubtitle: "Stores, distributors and manufacturers of electronic components on the Kvatronix platform",
    sellersEmpty: "No active sellers yet",
    sellerSearchPlaceholder: "Search seller products…",
    sellerShowMore: "+ show more",
    sellerStockLabel: "In stock",
    sellerOnOrderLabel: "On order",
    sellerStockSuffix: "pcs",
    // home — ai section
    poweredByAi: "POWERED BY AI",
    aiConsultantTitle: "AI consultant",
    aiConsultantDesc:
      "Describe your task — AI will pick the right components from the catalog. Works like an experienced engineer: knows specs, analogs and part compatibility.",
    aiFeature1: "💬 «Need a capacitor for a 12V 2A PSU filter»",
    aiFeature2: "🔍 Analog and replacement matching",
    aiFeature3: "📋 Datasheet recommendations",
    aiFeature4: "🇺🇦 Native Ukrainian support",
    aiAssistantName: "Kvatronix AI assistant",
    online: "Online",
    aiGreeting:
      "Describe your task — I'll suggest a component from the catalog, an analog, or specs.",
    aiChip1: "Pick a MOSFET for 24V 5A PWM",
    aiChip2: "BC547 analog",
    aiChip3: "1N4007 in stock?",
    aiInputPlaceholder: "Describe what you need...",
    aiSendAria: "Send",
    aiRateLimitPrefix: "Rate limit exceeded. Wait",
    aiRateLimitSuffix: "s",
    // home — why kvatronix
    whyTitle: "Why Kvatronix?",
    whySubtitle: "A modern platform for professionals and enthusiasts",
    whyItem1Title: "2ms search",
    whyItem1Desc: "MeiliSearch with faceted filters and part-number search",
    whyItem2Title: "AI product descriptions",
    whyItem2Desc:
      "GPT-4o generates descriptions, picks analogs and recommends compatible parts",
    whyItem3Title: "Nova Poshta",
    whyItem3Desc:
      "Automatic shipping calculation, waybill creation, parcel tracking",
    whyItem4Title: "Multichannel",
    whyItem4Desc:
      "Selling on Rozetka, Prom.ua, Hotline and Google Shopping simultaneously",
    // checkout (call-to-order MVP)
    coTitle: "Checkout",
    coSubtitle: "A manager will call within 30 min to confirm delivery and arrange payment.",
    coBackToCart: "← Back to cart",
    coContactInfo: "Contact",
    coName: "Full name",
    coNamePlaceholder: "John Smith",
    coPhone: "Phone",
    coEmail: "Email (optional)",
    coEmailHint: "If provided, we'll copy order details to your inbox.",
    coDelivery: "Delivery",
    coCity: "City",
    coCityPlaceholder: "Kyiv",
    coNotes: "Notes",
    coNotesPlaceholder: "Nova Poshta branch, packaging requirements, etc.",
    coTerms: "I agree to the Terms of Use and Privacy Policy",
    coSubmit: "Place order",
    coSubmitting: "Submitting…",
    coSummary: "Your order",
    coTotal: "Total",
    coError: "Could not place order. Try again or call: +380 99 042-21-13",
    coEmptyCart: "Cart is empty",
    coGoCatalog: "Browse catalog",
    coRequiredField: "required field",
    coInvalidPhone: "Invalid phone format",
    // order confirmation
    orderThanks: "Order received!",
    orderCallback: "A manager will call within 30 min to confirm delivery and arrange payment.",
    orderNumber: "Order number",
    orderDate: "Date",
    orderSummary: "Items",
    orderDelivery: "Delivery",
    orderShippingAddress: "Shipping address",
    orderContact: "Contact",
    orderShippingMethod: "Shipping method",
    orderPayment: "Payment",
    orderPaymentMethod: "Payment method",
    orderPaymentManual: "Arranged with manager",
    orderPaymentDue: "Amount due",
    orderHelpTitle: "Questions?",
    orderHelpCall: "Phone",
    orderHelpContacts: "Contacts",
    orderHelpReturns: "Returns & exchanges",
    orderBackToCatalog: "Browse catalog",
    // cart totals
    ctSubtotal: "Subtotal",
    ctShipping: "Shipping",
    ctTaxes: "Taxes",
    ctDiscount: "Discount",
    ctTotal: "Total",
    // checkout chrome
    chkBackToCart: "← Back to cart",
    chkRights: "All rights reserved.",
    // PDP
    pdpDescEmpty: "Manufacturer description available on request — contact a manager.",
    pdpDatasheet: "Datasheet PDF",
    pdpCopySku: "Copy SKU",
    pdpCopied: "Copied",
    pdpDeliveryTitle: "Delivery & terms",
    pdpDeliveryNova: "Nova Poshta on the day of payment",
    pdpDeliveryPickup: "Pickup in Kyiv",
    pdpDeliveryCallback: "Manager will call back to confirm",
    pdpTrustWarranty: "Warranty",
    pdpTrustOriginal: "Original components",
    pdpTrustConsult: "Technical consultation",
    pdpAddToCart: "Add to cart",
    pdpSelectVariant: "Select variant",
    pdpOutOfStock: "Out of stock",
    pdpSelectOptions: "Select options",
    pdpQty: "Qty",
  },
} as const

export type UiKey = keyof (typeof DICTIONARY)["ua"]

export function t(locale: string | undefined, key: UiKey): string {
  const lang: SupportedLocale =
    locale && isSupportedLocale(locale) ? locale : DEFAULT_LOCALE
  return DICTIONARY[lang][key] ?? DICTIONARY[DEFAULT_LOCALE][key]
}
